import type { AnyID, LinkedItem, SceneObjects } from '@types'
import {
  LATERAL_GROUP_GAP_MM,
  gapAlongSharedWall,
  getEffectiveWidth,
  getItemCenter,
} from '@utils/kitchenInspector/geometry'
import {
  getFurnitureWorldWorktopYMm,
  getRinconeroRegletaFrontReachMm,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { getRinconeroLinkDistance } from '@utils/sceneObjects/rinconeroGeometry'
import { getRinconeroHandedness, parseRinconeroCatalogCode } from '@utils/sceneObjects/snappedSides'
import { Matrix4, Quaternion, Vector3 } from 'three'

/** Grosor de catálogo del GLB Frontal (eje Z local, mm). */
export const FRONTAL_SLAB_THICKNESS_MM = 10

/** Dos unidades se consideran la misma altura de encimera (mm). */
export const FRONTAL_HEIGHT_RUN_EPS_MM = 2

export type FrontalItemLookup = {
  get(id: string): SceneObjects | undefined
}

export type FrontalHostRef = {
  id: AnyID
  width: number
}

export function isFrontalCatalog(catalogID?: string | number | null): boolean {
  return String(catalogID ?? '').toUpperCase() === 'FRONTAL'
}

/** Textura / zona de acabado de un frontal (la losa, no el mueble). */
export function isFrontalZoneTexture(texture?: {
  zone?: string
  zoneDisplayName?: string
  modelo?: string
} | null): boolean {
  if (!texture) return false
  return (
    texture.zone === 'Frontal' ||
    texture.zoneDisplayName === 'Frontal' ||
    texture.modelo === 'Frontal'
  )
}

export function isFrontalRegletaHost(item?: {
  catalogID?: string | number
  description?: string
} | null): boolean {
  if (!item) return false
  const catalog = String(item.catalogID ?? '').toUpperCase()
  return catalog.startsWith('RB') || Boolean(item.description?.toLowerCase().includes('regleta'))
}

/** MBJ pegado a pared: candidato a frontal. Las islas no. Las regletas sí suman ancho. */
export function isFrontalWallHost(item?: LinkedItem | null): boolean {
  if (!item || item.tipoNormalizado !== 'MBJ') return false
  // El rinconero toca dos paredes: aunque le falte snappedWall, sus dos brazos llevan frontal.
  if (getRinconeroHandedness(item.catalogID)) return true
  return !!item.snappedWall && item.snappedWall !== ''
}

/** Brazo de retorno de un rinconero: los MBJ cuentan aunque aún no hayan heredado pared. */
export function getFrontalRunsForRinconeroReturnArm(
  items: FrontalItemLookup,
  group: FrontalHostRef[],
): FrontalHostRef[][] {
  const hosts = group.filter((entry) => {
    const unit = items.get(entry.id) as LinkedItem | undefined
    return unit?.tipoNormalizado === 'MBJ' && !isFrontalRegletaHost(unit)
  })
  return splitFrontalHostsByWorktopY(items, hosts)
}

export function getEncimeraThicknessMm(encimera?: {
  textures?: Array<{ acabado?: string }>
  height?: number
} | null): number {
  if (!encimera) return 0
  const acabado = String(encimera.textures?.[0]?.acabado ?? '').toLowerCase()
  if (acabado.includes('porcelanicas 12')) return 12
  if (acabado.includes('porcelanicas 20')) return 20
  if (acabado.includes('postformadas')) return 30
  if (acabado.includes('compacto')) return 10
  return encimera.height ?? 0
}

export function findEncimeraSonOnUnit(
  items: FrontalItemLookup,
  unit?: LinkedItem | null,
): SceneObjects | null {
  if (!unit?.childs) return null
  for (const childId of unit.childs) {
    const child = items.get(childId)
    if (
      child?.type === 'linkedSon' &&
      ((child as { typeOfSon?: string }).typeOfSon === 'Encimera' ||
        child.description?.toLowerCase().includes('encimera'))
    ) {
      return child
    }
  }
  return null
}

export function getFrontalHostWidthMm(
  item: LinkedItem | undefined,
  fallbackWidth: number,
): number {
  if (!item) return fallbackWidth
  // Free-dim / KBR: morph.default puede ser la ref de catálogo (p.ej. 400) y
  // item.width el layout real (1000). El frontal tiene que cubrir el mueble.
  const live = getEffectiveWidth(item) || fallbackWidth
  const parsed = parseRinconeroCatalogCode(item.catalogID)
  if (parsed?.widthMm) {
    const doorW = parsed.doorWidthMm
    // A veces width/morph guarda el vano de puerta (400), no la carcasa (1000).
    if (doorW && Math.abs(live - doorW) <= 5 && parsed.widthMm > live + 50) {
      return parsed.widthMm
    }
    return live || parsed.widthMm
  }
  return live || fallbackWidth
}

/** Largo del frontal en el retorno: tope vivo (encimera/regleta), no carcasa de catálogo. */
export function getRinconeroReturnFrontalLengthMm(
  rinconero: LinkedItem,
  items?: FrontalItemLookup,
): number {
  const reach = getRinconeroRegletaFrontReachMm(rinconero)
  const thickness = Number(rinconero.thickness) || 0
  const encimera = items ? findEncimeraSonOnUnit(items, rinconero) : null
  const encLive = Number((encimera as { thickness?: number } | null)?.thickness ?? 0)
  const liveFront = Math.max(reach || 0, thickness)
  // encimeraCatalog/carcasa 720 no debe alargar el frontal más que el frente vivo.
  if (encLive > 0 && encLive <= liveFront + 30) return Math.max(encLive, liveFront)
  return liveFront
}

export type FrontalFinishSnapshot = {
  snappedWall?: AnyID
  x: number
  y: number
  z: number
  textures?: unknown
  zones?: unknown
  image?: unknown
}

export function snapshotFrontalFinishes(
  items: Map<string, SceneObjects> | Map<AnyID, SceneObjects>,
): FrontalFinishSnapshot[] {
  const snapshots: FrontalFinishSnapshot[] = []
  for (const item of items.values()) {
    if (!item || !('catalogID' in item) || !isFrontalCatalog(item.catalogID)) continue
    if (!('matrix' in item) || !item.matrix) continue
    const p = new Vector3()
    item.matrix.decompose(p, new Quaternion(), new Vector3())
    snapshots.push({
      snappedWall: 'snappedWall' in item ? (item.snappedWall as AnyID | undefined) : undefined,
      x: p.x,
      y: p.y,
      z: p.z,
      textures: 'textures' in item && item.textures ? JSON.parse(JSON.stringify(item.textures)) : undefined,
      zones: 'zones' in item && item.zones ? JSON.parse(JSON.stringify(item.zones)) : undefined,
      image: 'image' in item ? item.image : undefined,
    })
  }
  return snapshots
}

export function applyClosestFrontalFinish(
  frontal: LinkedItem,
  snapshots: FrontalFinishSnapshot[],
): boolean {
  if (!snapshots.length || !frontal.matrix) return false
  const p = new Vector3()
  frontal.matrix.decompose(p, new Quaternion(), new Vector3())
  let best: FrontalFinishSnapshot | null = null
  let bestD = Infinity
  for (const snap of snapshots) {
    if (
      snap.snappedWall &&
      frontal.snappedWall &&
      String(snap.snappedWall) !== String(frontal.snappedWall)
    ) {
      continue
    }
    const d = Math.hypot(p.x - snap.x, p.z - snap.z)
    if (d < bestD) {
      bestD = d
      best = snap
    }
  }
  if (!best || bestD > 2500) return false
  if (best.textures) frontal.textures = JSON.parse(JSON.stringify(best.textures)) as LinkedItem['textures']
  if (best.zones) (frontal as { zones?: unknown }).zones = JSON.parse(JSON.stringify(best.zones))
  if (best.image) frontal.image = best.image as LinkedItem['image']
  return true
}

export function copyHostEncimeraFinishToFrontal(
  frontal: LinkedItem,
  encimera: SceneObjects | null | undefined,
): boolean {
  if (!encimera || !('textures' in encimera) || !encimera.textures?.length) return false
  if (!frontal.textures?.length) return false
  const src =
    encimera.textures.find(
      (t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera',
    ) ?? encimera.textures[0]
  const idx = frontal.textures.findIndex(
    (t) => t.zone === 'Frontal' || t.zoneDisplayName === 'Frontal' || t.modelo === 'Frontal',
  )
  const i = idx >= 0 ? idx : 0
  const prev = frontal.textures[i]
  frontal.textures[i] = {
    ...prev,
    name: src.name ?? prev.name,
    url: src.url ?? prev.url,
    description: src.description ?? prev.description,
    acabado: src.acabado ?? prev.acabado,
    searchID: src.searchID ?? prev.searchID,
    id: src.id ?? prev.id,
    color: src.color ?? prev.color,
    roughness: src.roughness ?? prev.roughness,
    metalness: src.metalness ?? prev.metalness,
    envMapIntensity: src.envMapIntensity ?? prev.envMapIntensity,
    repeatX: src.repeatX ?? prev.repeatX,
    repeatY: src.repeatY ?? prev.repeatY,
    img: src.img ?? prev.img,
  }
  if (src.img) frontal.image = src.img
  const zoneName = prev.zoneName || frontal.textures[i].zoneName
  const zones = (frontal as { zones?: Record<string, { material?: Record<string, unknown> }> }).zones
  if (zoneName && zones?.[zoneName]) {
    const matPrev = zones[zoneName].material || {}
    zones[zoneName].material = {
      ...matPrev,
      name: src.name ?? matPrev.name,
      url: src.url ?? matPrev.url,
      acabado: src.acabado ?? matPrev.acabado,
      searchID: src.searchID ?? matPrev.searchID,
      id: src.id ?? matPrev.id,
      color: src.color ?? matPrev.color,
      roughness: src.roughness ?? matPrev.roughness,
      metalness: src.metalness ?? matPrev.metalness,
      envMapIntensity: src.envMapIntensity ?? matPrev.envMapIntensity,
      repeatX: src.repeatX ?? matPrev.repeatX,
      repeatY: src.repeatY ?? matPrev.repeatY,
      img: src.img ?? matPrev.img,
    }
  }
  return true
}

/**
 * Distancia real del giro en L al mueble del retorno que tiene pegado (para no pasarse del
 * tope). Se mide hasta el extremo del host más cercano a la esquina, no hasta su origen:
 * en el rinconero izquierdo el retorno corre hacia la esquina y el origen del host es su
 * extremo lejano.
 */
export function getRinconeroReturnExtraToHostMm(
  rinconero: LinkedItem,
  host: LinkedItem | undefined,
  fallbackMm: number,
): number {
  if (!host?.matrix || !rinconero.matrix) return fallbackMm
  const rPos = new Vector3()
  const rQuat = new Quaternion()
  rinconero.matrix.decompose(rPos, rQuat, new Vector3())
  const hPos = new Vector3()
  const hQuat = new Quaternion()
  host.matrix.decompose(hPos, hQuat, new Vector3())
  const rX = new Vector3(1, 0, 0).applyQuaternion(rQuat)
  const rZ = new Vector3(0, 0, 1).applyQuaternion(rQuat)
  const width = getFrontalHostWidthMm(rinconero, Number(rinconero.width) || 0)
  const hand = getRinconeroHandedness(rinconero.catalogID)
  const origin =
    hand === 'right' ? rPos.clone().addScaledVector(rX, width) : rPos.clone()
  const hostWidth = getFrontalHostWidthMm(host, Number(host.width) || 0)
  const hEnd = hPos.clone().addScaledVector(new Vector3(1, 0, 0).applyQuaternion(hQuat), hostWidth)
  const dists = [hPos, hEnd].map((point) => point.clone().sub(origin).dot(rZ)).filter((d) => d > 1)
  if (dists.length === 0) return fallbackMm
  return Math.min(Math.min(...dists), fallbackMm)
}

/**
 * Y mundo (mm) donde debe apoyarse el frontal de esta unidad.
 * Misma regla que el tramo de igual altura: tope de encimera / cuerpo + zócalo.
 */
export function getUnitFrontalWorktopYMm(
  items: FrontalItemLookup,
  unit?: LinkedItem | null,
): number {
  if (!unit) return 0
  const encimera = findEncimeraSonOnUnit(items, unit)
  return getFurnitureWorldWorktopYMm(unit, getEncimeraThicknessMm(encimera))
}

export function getRunFrontalWorktopYMm(
  items: FrontalItemLookup,
  run: FrontalHostRef[],
  fallbackY = 0,
): number {
  let maxY = 0
  for (const entry of run) {
    const unit = items.get(entry.id) as LinkedItem | undefined
    if (isFrontalRegletaHost(unit)) continue
    const y = getUnitFrontalWorktopYMm(items, unit)
    if (y > maxY) maxY = y
  }
  return maxY > 0 ? maxY : fallbackY
}

export function filterFrontalWallHosts(
  items: FrontalItemLookup,
  group: FrontalHostRef[],
): FrontalHostRef[] {
  return group.filter((entry) => isFrontalWallHost(items.get(entry.id) as LinkedItem | undefined))
}

/**
 * Parte un tramo ya ordenado (aunque estén snapped/colisionando) en rachas
 * consecutivas de la misma altura de encimera.
 * Misma altura en todo el tramo → una sola racha (un frontal).
 */
export function splitFrontalHostsByWorktopY(
  items: FrontalItemLookup,
  hosts: FrontalHostRef[],
  epsMm = FRONTAL_HEIGHT_RUN_EPS_MM,
): FrontalHostRef[][] {
  const runs: FrontalHostRef[][] = []
  let pendingRegletas: FrontalHostRef[] = []

  const worktopYOf = (entry: FrontalHostRef) =>
    getUnitFrontalWorktopYMm(items, items.get(entry.id) as LinkedItem | undefined)

  const firstNonRegletaY = (run: FrontalHostRef[]) => {
    const host = run.find((entry) => !isFrontalRegletaHost(items.get(entry.id) as LinkedItem | undefined))
    return host ? worktopYOf(host) : worktopYOf(run[0])
  }

  for (const entry of hosts) {
    const unit = items.get(entry.id) as LinkedItem | undefined
    if (isFrontalRegletaHost(unit)) {
      const prev = runs[runs.length - 1]
      if (prev) prev.push(entry)
      else pendingRegletas.push(entry)
      continue
    }

    const y = worktopYOf(entry)
    const prev = runs[runs.length - 1]
    if (!prev) {
      runs.push([...pendingRegletas, entry])
      pendingRegletas = []
      continue
    }
    if (Math.abs(firstNonRegletaY(prev) - y) <= epsMm) prev.push(entry)
    else runs.push([entry])
  }

  if (pendingRegletas.length > 0 && runs.length === 0) {
    runs.push(pendingRegletas)
  }
  return runs
}

export function getFrontalRunsForWallGroup(
  items: FrontalItemLookup,
  group: FrontalHostRef[],
): FrontalHostRef[][] {
  return splitFrontalHostsByWorktopY(items, filterFrontalWallHosts(items, group))
}

/**
 * Parte un brazo ya recto (mismo segmentIndex) si hay hueco real.
 * No se fía de snappedSides: al arrastrar por la pared el registro del rinconero
 * a veces sigue apuntando a la unidad suelta y el frontal cubría el hueco.
 */
export function splitFrontalArmByRealGap(
  items: Map<AnyID, SceneObjects>,
  group: FrontalHostRef[],
  gapThresholdMm = LATERAL_GROUP_GAP_MM,
): FrontalHostRef[][] {
  if (group.length <= 1) return group.length ? [group] : []

  const result: FrontalHostRef[][] = []
  let current: FrontalHostRef[] = [group[0]]

  for (let i = 1; i < group.length; i++) {
    const prevEntry = current[current.length - 1]
    const nextEntry = group[i]
    const prev = items.get(prevEntry.id)
    const next = items.get(nextEntry.id)

    if (!prev || !next) {
      result.push(current)
      current = [nextEntry]
      continue
    }

    const gapOnWall = gapAlongSharedWall(prev, next, items)
    const prevCenter = getItemCenter(prev)
    const nextCenter = getItemCenter(next)
    const centerDistance = Math.hypot(prevCenter.x - nextCenter.x, prevCenter.z - nextCenter.z)
    const effectiveGap =
      centerDistance - (getEffectiveWidth(prev) + getEffectiveWidth(next)) / 2
    const gap =
      gapOnWall !== null ? gapOnWall : Number.isFinite(effectiveGap) ? effectiveGap : 0

    if (gap > gapThresholdMm) {
      result.push(current)
      current = [nextEntry]
    } else {
      current.push(nextEntry)
    }
  }

  result.push(current)
  return result
}

/** Distancia XZ de un punto al segmento [a, b]. */
function distanceXZToSegment(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const abx = bx - ax
  const abz = bz - az
  const apx = px - ax
  const apz = pz - az
  const ab2 = abx * abx + abz * abz
  const t = ab2 > 0 ? Math.max(0, Math.min(1, (apx * abx + apz * abz) / ab2)) : 0
  return Math.hypot(px - (ax + t * abx), pz - (az + t * abz))
}

/** El frontal cubre la pared de estos muebles. No usar el ancho de la losa como radio:
 * una tira de 2 m del otro rincón “alcanzaba” este grupo y se borraba. */
export function frontalBelongsToHosts(
  frontal: { matrix?: Matrix4; width?: number } | null | undefined,
  hosts: Array<{ matrix?: Matrix4; width?: number; thickness?: number }>,
): boolean {
  if (!frontal?.matrix || hosts.length === 0) return false
  const fp = new Vector3()
  const fq = new Quaternion()
  frontal.matrix.decompose(fp, fq, new Vector3())
  const fX = new Vector3(1, 0, 0).applyQuaternion(fq)
  const frontalW = Number(frontal.width) || 0
  const fx2 = fp.x + fX.x * frontalW
  const fz2 = fp.z + fX.z * frontalW

  for (const host of hosts) {
    if (!host.matrix) continue
    const hp = new Vector3()
    const hq = new Quaternion()
    host.matrix.decompose(hp, hq, new Vector3())
    const hX = new Vector3(1, 0, 0).applyQuaternion(hq)
    const hostW = Number(host.width) || 0
    const corners = [
      [hp.x, hp.z],
      [hp.x + hX.x * hostW, hp.z + hX.z * hostW],
    ]
    for (const [cx, cz] of corners) {
      if (distanceXZToSegment(cx, cz, fp.x, fp.z, fx2, fz2) <= 120) return true
    }
  }
  return false
}

/** El tramo del retorno sigue pegado al giro en L del rinconero (no una unidad ya separada). */
export function isFrontalRunAttachedToRinconero(
  items: FrontalItemLookup,
  rinconero: LinkedItem,
  run: FrontalHostRef[],
): boolean {
  for (const entry of run) {
    const unit = items.get(entry.id) as LinkedItem | undefined
    if (!unit || isFrontalRegletaHost(unit)) continue
    if (getRinconeroLinkDistance(rinconero, unit) != null) return true
  }
  return false
}

/**
 * Extremo izquierdo del tramo (origen local +X del primer MBJ).
 * El ancla “primera no-regleta” no tiene por qué ser el origen del frontal.
 */
export function getFrontalRunStartPose(
  items: FrontalItemLookup,
  run: FrontalHostRef[],
): { position: Vector3; quaternion: Quaternion; thickness: number } | null {
  const hosts = run
    .map((entry) => items.get(entry.id) as LinkedItem | undefined)
    .filter((unit): unit is LinkedItem => Boolean(unit) && !isFrontalRegletaHost(unit))
  if (hosts.length === 0) return null

  const reference = hosts[0]
  const referencePosition = new Vector3()
  const referenceQuaternion = new Quaternion()
  reference.matrix.decompose(referencePosition, referenceQuaternion, new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(referenceQuaternion)

  let start = reference
  let startPosition = referencePosition.clone()
  let startAlong = 0
  for (const host of hosts) {
    const position = new Vector3()
    host.matrix.decompose(position, new Quaternion(), new Vector3())
    const along = new Vector3(
      position.x - referencePosition.x,
      0,
      position.z - referencePosition.z,
    ).dot(xAxis)
    if (along < startAlong - 0.5) {
      startAlong = along
      start = host
      startPosition = position
    }
  }

  const startQuaternion = new Quaternion()
  start.matrix.decompose(new Vector3(), startQuaternion, new Vector3())
  return {
    position: startPosition,
    quaternion: startQuaternion,
    thickness: start.thickness || 0,
  }
}

/** Origem do MBJ = fondo / pared. El frontal se queda ahí, no en el canto delantero. */
export function setFrontalMatrixWorktopY(matrix: Matrix4, frontalY: number): Matrix4 {
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  matrix.decompose(position, quaternion, scale)
  position.y = frontalY
  return matrix.compose(position, quaternion, scale)
}

export function composeFrontalWorldMatrix(
  start: { position: Vector3; quaternion: Quaternion },
  frontalY: number,
): Matrix4 {
  const position = start.position.clone()
  position.y = frontalY
  return new Matrix4().compose(position, start.quaternion, new Vector3(1, 1, 1))
}

export function getFrontalRunAnchor(items: FrontalItemLookup, run: FrontalHostRef[]): FrontalHostRef {
  return (
    run.find((entry) => !isFrontalRegletaHost(items.get(entry.id) as LinkedItem | undefined)) ??
    run[0]
  )
}

export function findFrontalRunContaining(
  runs: FrontalHostRef[][],
  unitId: AnyID,
): FrontalHostRef[] | undefined {
  return runs.find((run) => run.some((entry) => entry.id === unitId))
}

export function sumFrontalRunWidthMm(
  items: FrontalItemLookup,
  run: FrontalHostRef[],
): number {
  return run.reduce((total, entry) => {
    const item = items.get(entry.id) as LinkedItem | undefined
    return total + getFrontalHostWidthMm(item, entry.width)
  }, 0)
}

/**
 * Conserva el orden del tramo; si el ancla (rinconero) está más cerca del
 * último extremo, invierte para que la racha de esquina quede la primera.
 */
export function orderFrontalRunsFromAnchor(
  items: FrontalItemLookup,
  runs: FrontalHostRef[][],
  getDistanceToAnchor: (unit: LinkedItem) => number,
): FrontalHostRef[][] {
  if (runs.length <= 1) return runs
  const distOf = (run: FrontalHostRef[]) => {
    let min = Number.POSITIVE_INFINITY
    for (const entry of run) {
      const unit = items.get(entry.id) as LinkedItem | undefined
      if (!unit) continue
      min = Math.min(min, getDistanceToAnchor(unit))
    }
    return min
  }
  return distOf(runs[0]) <= distOf(runs[runs.length - 1]) ? runs : [...runs].reverse()
}
